import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, MapPin, Wallet, Shield, BookOpen, Crown, Menu, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTour } from '@/context/TourContext';

const TOUR_STEPS = [
  {
    page: '/',
    target: null,
    title: 'Bem-vindo ao Evite Dividas!',
    description: 'Vou te mostrar como usar cada funcionalidade do site. Vamos la?',
    icon: MapPin,
    position: 'center',
  },
  {
    page: '/',
    target: 'hero-start-cta',
    title: 'Comece sua Análise Grátis',
    description: 'Clique aqui para fazer um diagnóstico rápido e organizar seus gastos. Funciona sem cadastro!',
    icon: Wallet,
    position: 'top',
  },
  {
    page: '/expenses',
    target: null,
    title: 'Pagina do Organizador',
    description: 'Aqui voce adiciona despesas, define limite mensal, acompanha gastos por categoria e cria metas financeiras.',
    icon: Wallet,
    position: 'center',
  },
  {
    page: '/analyze',
    target: null,
    title: 'Analise de Dividas',
    description: 'Antes de parcelar ou financiar, nossa IA analisa se a decisao pode virar divida. Leva menos de 1 minuto!',
    icon: Shield,
    position: 'center',
  },
  {
    page: '/blog',
    target: null,
    title: 'Blog com Dicas',
    description: 'Conteudos praticos sobre financas pessoais para te ajudar a tomar melhores decisoes e sair das dividas.',
    icon: BookOpen,
    position: 'center',
  },
  {
    page: '/pricing',
    target: null,
    title: 'Planos e Precos',
    description: 'Comece gratis! Quando precisar de mais analises ou recursos premium, faca o upgrade aqui.',
    icon: Crown,
    position: 'center',
  },
  {
    page: '/',
    target: 'quick-nav-toggle',
    title: 'Menu Principal',
    description: 'Toque neste botao para abrir o menu com todas as funcionalidades do site.',
    icon: Menu,
    position: 'right',
  },
  {
    page: '/',
    target: 'bottom-nav-bar',
    title: 'Barra de Atalhos',
    description: 'Use esta barra para navegar rapidamente entre as principais secoes. Ela aparece em todas as paginas!',
    icon: Navigation,
    position: 'top',
  },
];

const GuidedTour = () => {
  const { isActive, currentStep, nextStep, prevStep, endTour } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [targetRect, setTargetRect] = useState(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const retryRef = useRef(null);

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const totalSteps = TOUR_STEPS.length;

  // Navigate to the correct page for the current step
  useEffect(() => {
    if (!isActive || !step) return;

    if (location.pathname !== step.page) {
      setIsNavigating(true);
      setTargetRect(null);
      navigate(step.page);
    }
  }, [currentStep, isActive, step, location.pathname, navigate]);

  // Find and position on the target element
  useEffect(() => {
    if (!isActive || !step) return;
    if (location.pathname !== step.page) return;

    // Clear any existing retry
    if (retryRef.current) {
      clearTimeout(retryRef.current);
      retryRef.current = null;
    }

    setIsNavigating(false);

    if (!step.target) {
      setTargetRect(null);
      return;
    }

    const findTarget = (attempts = 0) => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      } else if (attempts < 15) {
        retryRef.current = setTimeout(() => findTarget(attempts + 1), 200);
      } else {
        setTargetRect(null);
      }
    };

    const timer = setTimeout(() => findTarget(), 400);
    return () => {
      clearTimeout(timer);
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [currentStep, isActive, step, location.pathname]);

  // Recalculate on scroll/resize
  useEffect(() => {
    if (!isActive || !step?.target) return;

    const updateRect = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);
    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [isActive, step]);

  const handleNext = useCallback(() => {
    if (isLastStep) {
      endTour();
    } else {
      nextStep();
    }
  }, [isLastStep, endTour, nextStep]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) prevStep();
  }, [isFirstStep, prevStep]);

  if (!isActive || !step || isNavigating) return null;

  const Icon = step.icon;
  const hasSpotlight = step.target && targetRect;

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const viewW = window.innerWidth;
    const viewH = window.innerHeight;
    const tooltipWidth = Math.min(320, viewW - 32);
    const padding = 16;

    if (!hasSpotlight) {
      return {
        top: `${viewH / 2 - 120}px`,
        left: `${(viewW - tooltipWidth) / 2}px`,
        width: `${tooltipWidth}px`,
      };
    }

    switch (step.position) {
      case 'top':
        return {
          bottom: `${viewH - targetRect.top + padding}px`,
          left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, viewW - tooltipWidth - padding))}px`,
          width: `${tooltipWidth}px`,
        };
      case 'bottom':
        return {
          top: `${targetRect.top + targetRect.height + padding}px`,
          left: `${Math.max(padding, Math.min(targetRect.left + targetRect.width / 2 - tooltipWidth / 2, viewW - tooltipWidth - padding))}px`,
          width: `${tooltipWidth}px`,
        };
      case 'right':
        return {
          top: `${Math.max(padding, targetRect.top)}px`,
          left: `${Math.min(targetRect.left + targetRect.width + padding, viewW - tooltipWidth - padding)}px`,
          width: `${tooltipWidth}px`,
        };
      case 'left':
        return {
          top: `${Math.max(padding, targetRect.top)}px`,
          left: `${Math.max(padding, targetRect.left - tooltipWidth - padding)}px`,
          width: `${tooltipWidth}px`,
        };
      default:
        return {
          top: `${viewH / 2 - 120}px`,
          left: `${(viewW - tooltipWidth) / 2}px`,
          width: `${tooltipWidth}px`,
        };
    }
  };

  return (
    <AnimatePresence>
      {isActive && (
        <div className="fixed inset-0 z-[100]" data-testid="guided-tour-overlay">
          {/* Dark overlay */}
          {!hasSpotlight && (
            <motion.div
              className="absolute inset-0 bg-black/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Spotlight overlay using box-shadow */}
          {hasSpotlight && (
            <motion.div
              className="absolute rounded-xl z-[101]"
              style={{
                top: targetRect.top - 8,
                left: targetRect.left - 8,
                width: targetRect.width + 16,
                height: targetRect.height + 16,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.8)',
                pointerEvents: 'none',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Pulsing ring around spotlight */}
              <div className="absolute inset-0 rounded-xl border-2 border-[#00FF94]/60 animate-pulse" />
            </motion.div>
          )}

          {/* Click blocker for non-spotlight areas */}
          {hasSpotlight && (
            <div className="absolute inset-0 z-[100]" onClick={(e) => e.stopPropagation()} />
          )}

          {/* Tooltip Card */}
          <motion.div
            className="fixed z-[102]"
            style={getTooltipStyle()}
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            key={currentStep}
          >
            <div className="bg-[#0A0A0A] border border-[#00FF94]/30 rounded-2xl p-5 shadow-[0_0_40px_rgba(0,255,148,0.15)]">
              {/* Close button */}
              <button
                onClick={endTour}
                className="absolute top-3 right-3 text-gray-500 hover:text-white transition-colors"
                data-testid="tour-close-btn"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#00FF94]/20 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-[#00FF94]" />
                </div>
                <span className="text-xs text-gray-500 font-medium">
                  {currentStep + 1} de {totalSteps}
                </span>
              </div>

              {/* Content */}
              <h3 className="text-base font-bold text-white mb-2">{step.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">{step.description}</p>

              {/* Progress dots */}
              <div className="flex gap-1 mb-4 justify-center">
                {TOUR_STEPS.map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-1 rounded-full transition-all ${
                      idx === currentStep
                        ? 'w-6 bg-[#00FF94]'
                        : idx < currentStep
                          ? 'w-2 bg-[#00FF94]/40'
                          : 'w-2 bg-white/10'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation buttons */}
              <div className="flex items-center gap-2">
                {!isFirstStep && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePrev}
                    className="text-gray-400 hover:text-white text-xs px-3"
                    data-testid="tour-prev-btn"
                  >
                    <ChevronLeft className="w-3 h-3 mr-1" />
                    Anterior
                  </Button>
                )}
                <div className="flex-1" />
                {isFirstStep && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={endTour}
                    className="text-gray-500 hover:text-gray-300 text-xs px-3"
                    data-testid="tour-skip-btn"
                  >
                    Pular
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-[#00FF94] text-black hover:bg-[#00CC76] text-xs font-bold px-4 rounded-full"
                  data-testid="tour-next-btn"
                >
                  {isLastStep ? 'Concluir' : 'Proximo'}
                  {!isLastStep && <ChevronRight className="w-3 h-3 ml-1" />}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default GuidedTour;
